import { useEffect, useState } from "react";
import { Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const PRAYER_ORDER = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
const PRAYER_LABELS = { fajr: "Fajr", dhuhr: "Dhuhr", asr: "Asr", maghrib: "Maghrib", isha: "Isha" };

function parseTodayTime(timeStr, base = new Date()) {
  if (!timeStr) return null;
  const clean = String(timeStr).split(" ")[0];
  const m = clean.match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(base);
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  return d;
}

function findNextPrayer(times) {
  if (!times) return null;
  const today = new Date();
  for (const key of PRAYER_ORDER) {
    const t = parseTodayTime(times[key], today);
    if (t && t > today) return { key, time: t };
  }
  // All passed — next is tomorrow's Fajr
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const t = parseTodayTime(times.fajr, tomorrow);
  if (!t) return null;
  return { key: "fajr", time: t };
}

function formatCountdown(ms) {
  if (ms <= 0) return "Now";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

function format12(timeStr) {
  if (!timeStr) return "";
  const clean = String(timeStr).split(" ")[0];
  const [hh, mm] = clean.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(hh)) return timeStr;
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

export default function NextPrayerBadge() {
  const [prayerData, setPrayerData] = useState(null);
  const [next, setNext] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    api.get("/prayer-times")
      .then((r) => setPrayerData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!prayerData) return;
    // Use ATHAN times — same source as the PrayerTimesWidget below — so the two stay in sync.
    setNext(findNextPrayer(prayerData.athan || {}));
  }, [prayerData, now]);

  useEffect(() => {
    // 30-second tick is enough to keep an h/m countdown accurate
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  if (!next) return null;

  const diff = next.time.getTime() - now;
  const countdown = formatCountdown(diff);
  const athanTime = format12(prayerData?.athan?.[next.key]);
  const label = PRAYER_LABELS[next.key] || next.key;

  return (
    <Link
      to="/prayer-times"
      data-testid="next-prayer-badge"
      className="group inline-flex items-stretch rounded-2xl bg-cream/10 hover:bg-cream/15 backdrop-blur-md border border-gold/30 overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-gold/20"
    >
      <div className="flex items-center gap-3 px-5 py-3">
        <div className="relative w-10 h-10 shrink-0 flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-gold/20 animate-ping" />
          <span className="absolute inset-1 rounded-full bg-gold/90 text-navy flex items-center justify-center">
            <Clock size={14} />
          </span>
        </div>
        <div className="text-left">
          <div className="text-[9px] uppercase tracking-[0.3em] text-gold font-semibold">Next Prayer</div>
          <div className="font-serif text-xl text-cream mt-0.5 leading-none">{label} <span className="text-cream/60 text-sm font-sans">· {athanTime}</span></div>
        </div>
      </div>
      <div className="border-l border-gold/25 px-4 py-3 flex flex-col items-center justify-center bg-gold/5">
        <div className="text-[9px] uppercase tracking-[0.25em] text-gold">In</div>
        <div data-testid="next-prayer-countdown" className="font-serif text-xl text-cream leading-none mt-1 tabular-nums">{countdown}</div>
      </div>
      <div className="px-3 flex items-center text-gold/70 group-hover:text-gold group-hover:translate-x-0.5 transition-all">
        <ArrowRight size={14} />
      </div>
    </Link>
  );
}
